import { useEffect, useState, useCallback, useRef } from "react";
import { EmailSuggestInput } from "../../components/EmailSuggestInput";
import axios from "axios";
import { toast } from "sonner";
import { IdCard, Download, Trash2, Loader2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const fmtErr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : d?.msg || "Something went wrong.");

export const AdminIdCards = ({ user, onAuthFail, prefill, onPrefillUsed }) => {
  const [cards, setCards] = useState([]);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ name: "", designation: "", email: "", letter_id: "" });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/idcards");
      setCards(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error("Could not load ID cards");
    }
  }, [onAuthFail]);

  useEffect(() => {
    load();
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, name: prefill.name || "", designation: prefill.designation || "", letter_id: prefill.letter_id || "" }));
      toast.info(`Details prefilled for ${prefill.name} — add the company email and photo, then generate`);
      onPrefillUsed?.();
    }
  }, [prefill, onPrefillUsed]);

  const pickMember = (e) => {
    const m = team.find((t) => t.email === e.target.value);
    if (m) setForm((f) => ({ ...f, name: m.name || "", email: m.email }));
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5MB"); return; }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) { toast.error("Please upload the employee's photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("designation", form.designation);
      fd.append("email", form.email);
      fd.append("letter_id", form.letter_id || "");
      fd.append("photo", photo);
      await api.post("/idcards", fd);
      toast.success("ID card generated");
      setForm({ name: "", designation: "", email: "", letter_id: "" });
      setPhoto(null);
      setPhotoPreview("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const download = async (c, fmt) => {
    setRowBusy(c.id + fmt);
    try {
      const res = await api.get(`/idcards/${c.id}/download?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ID-Card-${c.name.replace(/\s+/g, "-")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download card");
    } finally { setRowBusy(""); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the ID card for ${c.name}?`)) return;
    setRowBusy(c.id + "del");
    try {
      await api.delete(`/idcards/${c.id}`);
      toast.success("ID card deleted");
      load();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setRowBusy(""); }
  };

  return (
    <div data-testid="admin-idcards">
      <div className="mb-6">
        <h2 className="text-lg text-white flex items-center gap-2"><IdCard size={18} className="text-nx-goldlight" /> Employee ID Card Generator</h2>
        <p className="text-xs text-slate-500 mt-1">Print-ready 54 × 85.6 mm card at 300 DPI — download as JPEG, JPG or PDF and send straight to your printing vendor.</p>
      </div>

      <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-6 mb-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="idcard-form">
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">Select From Team (optional)</span>
          <select onChange={pickMember} defaultValue="" className="mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none" data-testid="idcard-team-select">
            <option value="" disabled>Pick a team member…</option>
            {team.map((m) => <option key={m.email} value={m.email}>{`${m.name || m.email} — ${m.email}`}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">Employee Name <span className="text-nx-goldlight">*</span></span>
          <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none" data-testid="idcard-name" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">Designation <span className="text-nx-goldlight">*</span></span>
          <input required value={form.designation} onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))} placeholder="e.g. Field Engineer" className="mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none" data-testid="idcard-designation" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">Company Email <span className="text-nx-goldlight">*</span></span>
          <EmailSuggestInput required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="name@nexsecure.in" className="mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none" data-testid="idcard-email" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">Employee Photo (JPG/PNG/WEBP ≤5MB) <span className="text-nx-goldlight">*</span></span>
          <div className="mt-1 flex items-center gap-3">
            <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-xs px-4 py-2.5 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors" data-testid="idcard-photo-btn">
              <Upload size={13} /> {photo ? photo.name.slice(0, 24) : "Upload Photo"}
            </button>
            {photoPreview && <img src={photoPreview} alt="" className="h-11 w-9 object-cover border border-white/15" data-testid="idcard-photo-preview" />}
          </div>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={onPhoto} className="hidden" data-testid="idcard-photo-input" />
        </label>
        <div className="flex items-end">
          <button type="submit" disabled={busy} className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase px-6 py-3 bg-nx-gold text-nx-navy font-semibold hover:brightness-110 transition-all disabled:opacity-50" data-testid="idcard-submit">
            {busy && <Loader2 size={14} className="animate-spin" />} Generate ID Card
          </button>
        </div>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {cards.length === 0 && <p className="text-sm text-slate-500 border border-white/10 p-6 sm:col-span-2 lg:col-span-3 xl:col-span-4" data-testid="idcards-empty">No ID cards generated yet.</p>}
        {cards.map((c) => (
          <div key={c.id} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`idcard-card-${c.email}`}>
            <img src={`${API}/idcards/${c.id}/preview`} alt={`ID card for ${c.name}`} className="w-full border border-white/10" loading="lazy" data-testid="idcard-preview-img" />
            <p className="text-sm text-white mt-3">{c.name}</p>
            <p className="text-xs text-slate-500">{c.designation} · {c.email}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {["jpeg", "jpg", "pdf"].map((fmt) => (
                <button key={fmt} onClick={() => download(c, fmt)} disabled={rowBusy === c.id + fmt} className="flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase px-3 py-2 border border-nx-gold/40 text-nx-goldlight hover:bg-nx-gold/10 transition-colors disabled:opacity-50" data-testid={`idcard-download-${fmt}`}>
                  {rowBusy === c.id + fmt ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} {fmt.toUpperCase()}
                </button>
              ))}
              <button onClick={() => remove(c)} disabled={rowBusy === c.id + "del"} className="p-2 border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-500/40 transition-colors disabled:opacity-50 ml-auto" aria-label="Delete" data-testid="idcard-delete">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
